/*
 * GEMIANS — ATC footer runtime
 *
 * Fills the [price] / [shipping] / [frequency] / [interval] tokens under the
 * Add to Cart button, and drives the "BUY ONCE" switch.
 *
 * This file is the app-agnostic half. Where each value comes from lives in an
 * adapter registered against the contract at the bottom — see
 * assets/gemians-kaching-adapter.js for the Kaching Bundles one.
 *
 * Rules this file enforces, agreed with the client:
 *  - Values are COPIED VERBATIM from the app. Never parsed, never re-formatted,
 *    never computed. The app owns discounting, rounding and per-market money
 *    formatting; a second calculation would contradict it on the same page.
 *  - A value that cannot be read hides its whole line. A dash or a stale amount
 *    is worse than a missing line, because a stale amount looks correct.
 *  - Until an adapter is registered nothing is touched, so shipping this file
 *    on its own changes nothing on the page.
 */
(function () {
  'use strict';

  var ROOT = '.gemians-atc-footer';
  var DEBOUNCE_MS = 60;

  var adapter = null;
  var observer = null;
  var timer = null;

  function els(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  // The dash placeholder is kept so a line can be restored if a later read
  // succeeds — refresh() must be idempotent, it runs on every widget mutation.
  function placeholderOf(span) {
    if (!span.hasAttribute('data-gemians-placeholder')) {
      span.setAttribute('data-gemians-placeholder', span.textContent);
    }
    return span.getAttribute('data-gemians-placeholder');
  }

  function readToken(name) {
    if (!adapter || typeof adapter[name] !== 'function') return undefined;
    try {
      var value = adapter[name]();
      // Only a non-empty string counts. null/undefined/'' all mean "unreadable".
      return typeof value === 'string' && value.trim() !== '' ? value : null;
    } catch (e) {
      return null;
    }
  }

  function refreshLine(line) {
    var names = (line.getAttribute('data-gemians-tokens') || '').split(',').filter(Boolean);
    var readable = true;
    var values = {};

    names.forEach(function (name) {
      var value = readToken(name);
      if (value === undefined) return; // no adapter for it yet — leave alone
      if (value === null) readable = false;
      else values[name] = value;
    });

    if (!readable) {
      line.hidden = true;
      return;
    }
    line.hidden = false;

    els('[data-gemians-token]', line).forEach(function (span) {
      var name = span.getAttribute('data-gemians-token');
      placeholderOf(span);
      if (Object.prototype.hasOwnProperty.call(values, name)) {
        span.textContent = values[name];
      }
    });
  }

  function refresh() {
    els(ROOT).forEach(function (root) {
      els('[data-gemians-tokens]', root).forEach(refreshLine);
    });
    refreshSwitch();
    // An adapter may need to mirror app state elsewhere on the page (see the
    // sync() note in the contract). Runs on every refresh so it also catches
    // state the shopper changed inside the app's own UI, not just through us.
    if (adapter && typeof adapter.sync === 'function') {
      try {
        adapter.sync();
      } catch (e) {
        /* app state is the app's problem, never ours to crash on */
      }
    }
  }

  function currentOneTime() {
    if (!adapter || typeof adapter.isOneTime !== 'function') return null;
    try {
      var value = adapter.isOneTime();
      return typeof value === 'boolean' ? value : null;
    } catch (e) {
      return null;
    }
  }

  // The label names the action, so it flips with the mode: while subscribed it
  // offers "buy once", and in one-time mode it offers "subscribe". Driven off
  // the app's real state rather than off our own clicks, because the shopper
  // can change mode in the app's own UI too.
  function refreshSwitch() {
    var oneTime = currentOneTime();
    if (oneTime === null) return;
    els(ROOT).forEach(function (root) {
      els('[data-gemians-atc-switch]', root).forEach(function (button) {
        button.setAttribute('aria-pressed', String(oneTime));
        var label = oneTime
          ? button.getAttribute('data-gemians-label-one-time')
          : button.getAttribute('data-gemians-label-subscribe');
        if (label && button.textContent.trim() !== label) button.textContent = label;
      });
    });
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, DEBOUNCE_MS);
  }

  function bindSwitch() {
    els(ROOT).forEach(function (root) {
      els('[data-gemians-atc-switch]', root).forEach(function (button) {
        if (button.dataset.gemiansBound) return;
        button.dataset.gemiansBound = '1';
        button.addEventListener('click', function () {
          if (!adapter || typeof adapter.onSwitch !== 'function') return;
          var now = currentOneTime();
          var target = now === null ? button.getAttribute('aria-pressed') !== 'true' : !now;
          try {
            adapter.onSwitch(target);
          } catch (e) {
            return; // leave the button showing the state it still is in
          }
          // Don't guess the result — the app re-renders and refresh() reads the
          // real state back, so the label can never drift out of sync with it.
          scheduleRefresh();
        });
      });
    });
  }

  // The bundle widget renders and re-renders on its own schedule: it is not
  // there at DOMContentLoaded, and it rewrites itself when a tier is picked or
  // the mode changes. Watching the product column covers both without needing
  // to know any of the app's own events.
  function observe() {
    if (observer || typeof MutationObserver === 'undefined') return;
    var scope = document.querySelector('product-info') || document.body;
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(scope, { childList: true, subtree: true, characterData: true });
  }

  function start() {
    try {
      if (!document.querySelector(ROOT)) return;
      bindSwitch();
      observe();
      refresh();
    } catch (e) {
      /* never let this break the product page */
    }
  }

  /*
   * Adapter contract
   * ----------------
   * register({
   *   price:     () => string | null,   // exactly as the app prints it, symbol included
   *   shipping:  () => string | null,
   *   frequency: () => string | null,   // e.g. "56 days" — unit word included
   *   interval:  () => string | null,   // e.g. "2 months"
   *   onSwitch:  (oneTime: boolean) => void,
   *   sync:      () => void,            // optional, runs on every refresh
   *   isOneTime: () => boolean | null,  // optional, drives the switch label
   * })
   *
   * Return null when the value cannot be read — that hides the line, which is
   * the correct failure. Omit a key entirely and its token is left untouched.
   * Never return a number, and never build the string yourself.
   */
  var api = {
    register: function (next) {
      adapter = next || null;
      bindSwitch();
      refresh();
    },
    refresh: refresh,
  };

  window.Gemians = window.Gemians || {};
  window.Gemians.atcFooter = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', scheduleRefresh);
})();
